import React from 'react';
import { Select } from 'antd';
import { gql, useQuery } from '@apollo/client';
import { debounce } from 'lodash';
import {
  SiteSpecialityQueryQuery,
  SiteSpecialityQueryQueryVariables
} from '../../../../gql/typings';
import { Locale } from '../../../../localization/LocalizationKeys';
import { useLocalization } from '../../../util/useLocalization';

type SiteSpecialityFormInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  countryCode: string;
  disabled?: boolean;
};

const SiteSpecialityFormInput: React.FC<SiteSpecialityFormInputProps> = ({
  value,
  onChange,
  disabled,
  countryCode
}) => {
  const localization = useLocalization();
  const {
    data,
    loading,
    refetch,
  } = useQuery<SiteSpecialityQueryQuery, SiteSpecialityQueryQueryVariables>(SITE_SPECIALITY_DATA_QUERY,
    { variables: { countries: countryCode ? [countryCode] : [], search: '%' } });
  const searchDebounced = debounce(
    search => {
      const input: SiteSpecialityQueryQueryVariables = { search: `%${search}%` };
      if (countryCode) input.countries = [countryCode];
      return refetch(input);
    },
    25,
  );

  return (
    <Select
      placeholder={localization.formatMessage(Locale.Command.Select_Site_Specialty)}
      mode='multiple'
      style={{ minWidth: 120 }}
      loading={loading}
      value={value as string|undefined}
      showSearch
      onSearch={searchDebounced}
      filterOption={false}
      onChange={e => {
        onChange?.(e);
        searchDebounced('');
      }}
      disabled={disabled}
      allowClear
    >
      {
        (data?.siteSpecialties.nodes ?? []).map(sp => (
          <Select.Option key={sp!.code} value={sp!.code}>
            {
              sp!.label
            }
          </Select.Option>
        ))
      }
    </Select>
  );
};

const SITE_SPECIALITY_DATA_QUERY = gql`
  query SiteSpecialityQuery($countries: [CountryCode!], $search: String) {
    siteSpecialties(criteria:{
      label: $search,
      countries: $countries,
      fetchSize: {limit: 1000}
    }) {
      hash
      nodes {
        code
        label: displayLabel
        type
      }
    }
  }
`;

export default SiteSpecialityFormInput;
